import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { ActionButton, ButtonModel } from '@notino/react-styleguide';

import messages from '@containers/ProductDetailContainer/messages';
import { useFeatureFlags } from '@context/launch-darkly/LDProvider';

import { LinkSharer } from '../../components/LinkSharer';
import { GTM_LIST_LABEL } from '../../constants';
import { WishlistHeader } from '../../header';
import { Container, ButtonWrapper, Spacer } from '../../styled';
import AnimatedProductList from '../AnimatedProductList';

import { trackWishlistPageView, useTrackToCartAsync } from './tracking';
import { useWishlistedProducts } from './useWishlistedProducts';

export interface IWishlistPagerProps {
  productIds: string[];
  pageSize: number;
  userId: string; // logged-in user
  owner?: string; // id of foreign shared wishlist. In this case productIds are from the shared wishlist and further sharing is disabled
}

export const WishlistPager: React.FC<IWishlistPagerProps> = ({
  productIds,
  userId,
  pageSize,
  owner,
}) => {
  const { spaRedirectWishlistToPd } = useFeatureFlags();

  const {
    products,
    fetchMore,
    setProductWishlisted,
    loading,
    error,
    hasMore,
    isEmpty,
  } = useWishlistedProducts(productIds, !!owner, { pageSize });

  const trackAddToCartAsync = useTrackToCartAsync(productIds, GTM_LIST_LABEL);
  const hasProductsInWishlist = productIds.length === 0;

  return (
    <Container>
      <WishlistHeader
        isEmpty={hasProductsInWishlist}
        showLoginText={!hasProductsInWishlist && userId === '0' && !owner}
        isShared={!!owner}
      />

      {hasProductsInWishlist || owner ? (
        <Spacer isSmall={!!owner} />
      ) : (
        <LinkSharer />
      )}

      {!isEmpty && (
        <AnimatedProductList
          products={products}
          owner={owner}
          showAddToCartModal={false}
          canSpaRedirect={spaRedirectWishlistToPd}
          productItemProps={{
            GTMEventLabel: GTM_LIST_LABEL,
            sendGTM: true,
            noPriceIfUnavailable: true,
            showBuyButton: true,
            trackAddToCartAsync,
            onClosingCartModal: trackWishlistPageView,
            setProductWishlisted,
            inSectionOfType: 'wishlist',
            wishlistActionLocation: 'wishlist',
          }}
        />
      )}

      {hasMore && (
        <ButtonWrapper>
          <ActionButton
            buttonStyle={ButtonModel.Styles.secondary}
            buttonSize={ButtonModel.Sizes.medium}
            loading={loading}
            error={Boolean(error)}
            onClick={fetchMore}
          >
            <FormattedMessage {...messages.showNext} />
          </ActionButton>
        </ButtonWrapper>
      )}
    </Container>
  );
};
